/* ═══════════════════════════════════════════════════════════════
   DIY PRINTZ — Contract Cart Lock
   Detects contract-order line items and replaces qty controls
   with EDIT ORDER / DELETE ORDER buttons.

   Contract orders use "cents-as-quantity" pricing: the variant is
   $0.01 and the quote total is encoded as `quantity = total * 100`.
   NEVER reset the quantity — doing so destroys the customer's total.
   ═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  const CONTRACT_HANDLE = 'contract-dtf-print-press';
  const ORDER_FORM_URL = '/pages/contract-dtf-order';

  function fmt(n) { return '$' + Number(n).toFixed(2); }

  async function fetchCart() {
    // Read-only inspection must not restart AIOD's cart/discount pipeline.
    // AIOD explicitly ignores requests carrying its csapp=aiod marker.
    const r = await fetch('/cart.js?csapp=aiod&diy_contract_read=1', { headers: { 'Accept': 'application/json' }});
    if (!r.ok) throw new Error('Could not read the contract cart.');
    return r.json();
  }

  function isContractItem(item) {
    const props = item.properties || {};
    if (props._shipping_weight_line === 'true') return false;
    return item.handle === CONTRACT_HANDLE
      || props._contract_service === 'DTG'
      || props['_contract_token']
      || props._contract_token
      || props['_draft_token']
      || props._draft_token
      || props['_workorder_id']
      || props._workorder_id;
  }

  function findContractItems(cart) {
    return cart.items
      .map((item, index) => ({ ...item, _index: index }))
      .filter(isContractItem);
  }

  async function decorateContractLines() {
    const cart = await fetchCart();
    const contractItems = findContractItems(cart);

    // Toggle a body-level flag so CSS can hide theme-specific qty steppers /
    // Remove links that live OUTSIDE the .is-contract element. In the DIY
    // Printz theme, .eb-qty and .eb-line__remove are siblings of the price
    // cell that gets .is-contract, so a descendant selector on .is-contract
    // won't reach them. Body-level flag sidesteps that.
    document.body.classList.toggle('contract-cart-active', contractItems.length > 0);

    if (contractItems.length === 0) return;
    const physicalCount = cart.items.reduce((n, item) => {
      if ((item.properties || {})._shipping_weight_line === 'true') return n;
      return n + (isContractItem(item) ? Number(item.properties._total_garments || item.properties['Total Garments'] || item.properties['Total garments'] || 0) : item.quantity);
    }, 0);
    document.querySelectorAll('.eb-cart__count').forEach(el => { el.textContent = String(physicalCount); });
    if (cart.items.every(item => isContractItem(item) || (item.properties || {})._shipping_weight_line === 'true')) {
      document.querySelectorAll('.eb-job--retail .eb-job__label').forEach(el => { el.textContent = 'CONTRACT ORDER'; });
      document.querySelectorAll('.eb-job--retail .eb-job__meta').forEach(el => { el.textContent = physicalCount + ' GARMENTS'; });
    }

    contractItems.forEach(item => {
      const nodes = findLineDom(item);
      if (!nodes.length) return;

      nodes.forEach(node => {
        if (node.dataset.contractDecorated === '1') return;
        node.dataset.contractDecorated = '1';
        node.classList.add('is-contract', 'cart-item');
        renderContractSummary(node, item);
        renderContractControls(node, item);
      });
    });
  }

  function findLineDom(item) {
    const key = item.key || item.id;
    const exact = Array.from(document.querySelectorAll('[data-eb-line][data-line-key]')).filter(el=>el.dataset.lineKey===key);
    if(exact.length)return exact;
    const nodes = [];

    document.querySelectorAll(
      [
        `[data-cart-item-key="${key}"]`,
        `[data-line-item-key="${key}"]`,
        `[data-key="${key}"]`,
        `[data-variant-id="${item.variant_id}"]`,
        `[data-eb-line]`,
        `tr[id^="CartItem-"]`,
        `.cart__row`,
        `.cart-item`,
        `.cst-cart-item`
      ].join(',')
    ).forEach(el => {
      const inputKey = el.querySelector('input[name="updates[]"], input[name*="quantity"]');
      const dataKey = el.dataset.cartItemKey || el.dataset.lineItemKey || el.dataset.key || '';
      if (
        dataKey === key ||
        (inputKey && (inputKey.dataset.key === key || inputKey.dataset.cartItemKey === key)) ||
        el.querySelector(`a[href*="${item.handle}"]`) ||
        el.textContent.includes(item.title)
      ) {
        nodes.push(el);
      }
    });

    // Dedupe by identity, then drop any node that CONTAINS or IS-CONTAINED-BY
    // another matched node — loose selectors match a row and its inner wrapper.
    // Keep the OUTERMOST match.
    const uniq = Array.from(new Set(nodes));
    return uniq.filter(n => !uniq.some(o => o !== n && o.contains(n)));
  }

  function renderContractSummary(node, item) {
    const props = item.properties || {};
    if (node.querySelector('.contract-line-summary')) return;

    const dl = document.createElement('dl');
    dl.className = 'contract-line-summary';

    const rows = [
      ['Company',  props['Company']],
      ['Contact',  props['Contact']],
      ['Job',      props['Order Name'] || props['Job Name']],
      ['PO',       props['PO Number']],
      ['Groups',  props['Garment Groups'] || props['Designs']],
      ['Sizes',    props['Print Sizes']],
      ['Garments', props['Total Garments']],
      ['Quote',    props['Quote Total']],
      ['Setup',    props['Small-order setup']]
    ].filter(([, v]) => v);

    rows.forEach(([k, v]) => {
      const dt = document.createElement('dt'); dt.textContent = k;
      const dd = document.createElement('dd'); dd.textContent = v;
      dl.append(dt, dd);
    });
    if(props._contract_service==='DTG'&&/^https:\/\//.test(props['Production manifest']||'')){
      const dt=document.createElement('dt');dt.textContent='Production PDF';
      const dd=document.createElement('dd'),a=document.createElement('a');
      a.href=props['Production manifest'];a.textContent='Download work order';a.target='_blank';a.rel='noopener';dd.append(a);dl.append(dt,dd);
    }
    // Permanent artwork URLs are also public line properties for checkout/admin.
    // Compact previews here never replace those original download links.
    try {
      const artworks = JSON.parse(props._contract_artwork || '[]');
      if (Array.isArray(artworks) && artworks.length) {
        const dt = document.createElement('dt'); dt.textContent = 'Artwork by location';
        const dd = document.createElement('dd'); dd.className = 'contract-location-artwork';
        artworks.forEach(art => {
          if (!/^https:\/\//i.test(art.url || '')) return;
          const link = document.createElement('a');
          link.href = art.url; link.target = '_blank'; link.rel = 'noopener';
          if (art.image) {
            const img = document.createElement('img'); img.src = art.url; img.loading = 'lazy';
            img.alt = `Group ${art.group} ${art.placement} artwork`; img.width = 40; img.height = 40;
            img.addEventListener('error', () => { img.hidden = true; }); link.appendChild(img);
          }
          const label = document.createElement('span');
          label.textContent = `${art.group==='DTG'?'DTG':'G'+art.group} · ${art.placement.replace(/_/g, ' ')} · ${art.name} · ${art.size}`;
          link.appendChild(label); dd.appendChild(link);
        });
        dl.append(dt, dd);
      }
    } catch (_) {}

    const titleEl = node.querySelector(
      '.eb-line__title, .cart-item__name, .cart-item__title, .cst-cart-item__title, h2, h3, .product-title'
    ) || node;
    titleEl.after ? titleEl.after(dl) : node.appendChild(dl);
    if(props._contract_service==='DTG'){
      const list=node.querySelector('.eb-line__props');
      if(list&&!list.closest('details')){
        const details=document.createElement('details'),summary=document.createElement('summary');
        details.className='contract-full-details';summary.textContent='Full job details';
        list.before(details);details.append(summary,list);
      }
    }
  }

  function renderContractControls(node, item) {
    if (node.querySelector('.contract-line-ctrls')) return;

    const bar = document.createElement('div');
    bar.className = 'contract-line-ctrls';

    const editBtn = document.createElement('button');
    editBtn.type = 'button';
    editBtn.className = 'contract-edit-btn';
    editBtn.textContent = 'EDIT ORDER';
    editBtn.addEventListener('click', () => editContractOrder(item));

    const delBtn = document.createElement('button');
    delBtn.type = 'button';
    delBtn.className = 'contract-delete-btn';
    delBtn.textContent = 'DELETE ORDER';
    delBtn.addEventListener('click', () => deleteContractOrder(item));

    bar.append(editBtn, delBtn);

    const qtyEl = node.querySelector('.cart-item__quantity, .quantity, .cst-qty, [data-quantity-selector]');
    if (qtyEl && qtyEl.parentNode) {
      qtyEl.parentNode.insertBefore(bar, qtyEl);
    } else {
      node.appendChild(bar);
    }
  }

  function editContractOrder(item) {
    const props = item.properties || {};
    const token = props['_contract_token'] || props._contract_token
                || props['_draft_token']    || props._draft_token
                || props['_workorder_id']   || props._workorder_id
                || '';
    const url = new URL(props._contract_service === 'DTG' ? '/pages/contract-dtg-order' : ORDER_FORM_URL, location.origin);
    if(token)url.searchParams.set('edit',token);
    if(window.Shopify?.theme?.role==='unpublished')url.searchParams.set('preview_theme_id',window.Shopify.theme.id);
    location.href = url.href;
  }

  async function deleteContractOrder(item) {
    if (!confirm('DELETE CONTRACT ORDER?\n\nThis removes this job, including its print charge and shipping-weight line. Other cart items stay in your cart.')) return;

    try {
      const cart=await fetchCart(), token=item.properties?._draft_token||item.properties?._contract_token||item.properties?._workorder_id;
      const updates={};
      cart.items.forEach(line=>{
        const p=line.properties||{};
        const same=token&&(p._draft_token||p._contract_token||p._workorder_id)===token;
        // Legacy DTF uses an untagged weight line and supports one DTF job.
        const legacyWeight=item.handle===CONTRACT_HANDLE&&p._shipping_weight_line==='true'&&!p._draft_token&&!p._contract_token;
        if(line.key===item.key||same||legacyWeight)updates[line.key]=0;
      });
      const payload={updates};
      if(item.handle===CONTRACT_HANDLE&&cart.attributes?.contract_token===token)payload.attributes={contract_order:'',contract_job:'',contract_po:'',contract_company:'',contract_contact:'',contract_dropoff:'',contract_garments:'',contract_token:'',contract_workorder_url:'',quote_total:''};
      const r=await fetch('/cart/update.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
      if(!r.ok)throw new Error('Could not remove the complete job. Please retry.');
      location.reload();
    }catch(e){alert(e.message);}
  }

  // ─── Init ────────────────────────────────────────────────────
  // IMPORTANT: we do NOT intercept quantity change events any more.
  // Contract orders use cents-as-quantity — the quantity IS the price.
  // Resetting qty to 1 would silently destroy the customer's total.
  // The quantity stepper is hidden by contract-cart-lock.css instead,
  // so customers can't manipulate it.
  function init() {
    if (!location.pathname.includes('/cart')) {
      // DTG charge and weight must never be edited separately in a generic drawer.
      // Retail-only carts retain the original drawer and its app behavior.
      document.addEventListener('click',function(e){
        if(!e.target.closest('[data-testid="cart-drawer-trigger"]'))return;
        if(!document.querySelector('[data-dtg-contract-present]'))return;
        e.preventDefault();e.stopImmediatePropagation();
        const destination=new URL('/cart',location.origin);
        if(window.Shopify?.theme?.role==='unpublished')destination.searchParams.set('preview_theme_id',window.Shopify.theme.id);
        location.href=destination.href;
      },true);
      fetchCart().then(cart=>{
        if(cart.items.some(x=>x.properties?._contract_service==='DTG')){
          document.body.setAttribute('data-dtg-contract-present','true');
        }
      }).catch(console.error);
      return;
    }
    decorateContractLines().catch(console.error);
    // Re-decorate after theme AJAX cart updates
    // Only new/replaced cart rows need decorating, not every countdown tick,
    // app price rewrite, or coupon-field update elsewhere on the page.
    const rowSelector = '[data-eb-line], .cart-items__table-row, [data-line-item-key], tr[id^="CartItem-"], .cart__row, .cart-item, .cst-cart-item';
    let timer;
    const observer = new MutationObserver(records => {
      const rowsAdded = records.some(record => Array.from(record.addedNodes).some(node =>
        node.nodeType === 1 && (node.matches(rowSelector) || node.querySelector(rowSelector))
      ));
      if (!rowsAdded) return;
      clearTimeout(timer);
      timer = setTimeout(() => decorateContractLines().catch(console.error), 100);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
